import { ThemeOptions } from "@mui/material";
import { getColorFromPaletteColorOptions, palette } from "./palette";
import { fontFamily } from "./typography";
import { AngleSmallDownIcon } from "../icons/AngleSmallDownIcon";
import { createElement } from "react";
import { getFocusOutlineColor, getFocusOutlineSx } from "./focusStyles";

export const components: ThemeOptions['components'] = {
    MuiCssBaseline: {
        styleOverrides: fontFamily,
    },
    MuiTextField: {
        defaultProps: {
            variant: 'filled',
            InputProps: {
                disableUnderline: true,
                fullWidth: true,
            },
        },
        styleOverrides: {
            root: ({ theme }) => ({
                '&:has(.MuiInputLabel-root.MuiFormLabel-filled) .MuiFilledInput-root': {
                    backgroundColor: "transparent"
                },
                '& .MuiFilledInput-root': {
                    backgroundColor: palette.grey[50],
                    border: `1px solid ${palette.grey[200]}`,
                    borderRadius: '7px',
                    height: 60,
                    transition: 'border-color 0.3s ease',
                    '&:hover': {
                        borderColor: palette.grey[500],
                        backgroundColor: palette.grey[50],
                    },
                    '&.Mui-focused': {
                        outline: `3px solid ${getFocusOutlineColor(theme)}`,
                        outlineOffset: 0,
                    },
                    '& .MuiFilledInput-input': {
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        fontWeight: 500,
                        lineHeight: "148%",
                        fontSize: 16,
                    },
                    '&.Mui-error': {
                        borderColor: getColorFromPaletteColorOptions(palette.primary, 'main'),
                    }
                },
                '& .MuiFormLabel-root': {
                    transform: 'translate(16px, 18px)',
                    fontSize: 15,
                    lineHeight: "148%",
                    fontWeight: 400,
                    color: palette.text.primary,
                    '&.Mui-error': {
                        color: palette.text.primary,
                    },
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                        fontSize: 13,
                        lineHeight: "132%",
                        fontWeight: 400,
                        letterSpacing: 0,
                        transform: 'translate(16px, 10px) scale(0.8667)',
                        color: palette.grey[600]
                    }
                },
                '& input:-webkit-autofill, ': {
                    'transition': 'background-color 5000s ease-in-out 0s',
                },
                '& .MuiFilledInput-root.Mui-disabled': {
                    backgroundColor: `${palette.grey[100]}!important`,
                    border: "none !important",
                },
                '& .MuiInputAdornment-root .MuiIconButton-root': {
                    padding: 16,
                }
            }),
        },
    },
    MuiFilledInput: {
        styleOverrides: {
            root: {
                "&::before": {
                    borderBottom: "none !important",
                },
            },
        },
    },
    MuiAutocomplete: {
        defaultProps: {
            popupIcon: createElement(AngleSmallDownIcon, { width: 16, height: 16, color: palette.common.black }),
        },
    },
    MuiAlert: {
        styleOverrides: {
            root: {
                borderRadius: "5px",
                padding: "16px",
                "&.MuiAlert-colorInfo": {
                    backgroundColor: getColorFromPaletteColorOptions(palette.info, 'light'),
                    border: `1px solid ${getColorFromPaletteColorOptions(palette.info, 'main')}`
                },
                "&.MuiAlert-colorError": {
                    backgroundColor: getColorFromPaletteColorOptions(palette.error, 'light'),
                    border: `1px solid ${getColorFromPaletteColorOptions(palette.error, 'dark')}`,
                    color: palette.text.primary,
                },
                "& .MuiAlert-icon": {
                    display: "none"
                }
            },
        },
    },
    MuiAlertTitle: {
        styleOverrides: {
            root: {
                fontSize: 16,
                lineHeight: "148%",
                fontWeight: 500,
                color: palette.text.primary,
            },
        },
    },
    MuiButton: {
        defaultProps: {
            variant: 'contained',
            color: 'primary',
            disableElevation: true,
            disableRipple: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: "7px",
                padding: "12px 16px",
                lineHeight: 1,
                "&.MuiButton-sizeLarge": {
                    fontSize: 16,
                    lineHeight: "148%",
                    fontWeight: 500,
                    letterSpacing: 0,
                    height: 50,
                },
                "&.MuiButton-colorSecondary": {
                    color: palette.common.black,
                },
                ...getFocusOutlineSx(theme),
            }),
        }
    },
    MuiIconButton: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                ...getFocusOutlineSx(theme),
            }),
        }
    },
    MuiLink: {
        styleOverrides: {
            root: ({ theme }) => ({
                fontSize: 15,
                lineHeight: "148%",
                fontWeight: 400,
                "&:hover": {
                    color: theme.palette.primary.dark,
                },
                ...getFocusOutlineSx(theme, { borderRadius: "2px" }),
            }),
        }
    },
    MuiDivider: {
        styleOverrides: {
            root: {
                "::before, ::after": {
                    borderTopColor: palette.divider,
                }
            }
        }
    },
    MuiCheckbox: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                color: palette.grey[400],
                "&.Mui-focusVisible .MuiSvgIcon-root, &:focus-visible .MuiSvgIcon-root, &:active .MuiSvgIcon-root": {
                    outline: `3px solid ${getFocusOutlineColor(theme)}`,
                    outlineOffset: 1,
                    borderRadius: 4,
                },
            }),
        }
    },
    MuiRadio: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                color: palette.grey[400],
                "&.Mui-focusVisible .MuiSvgIcon-root, &:focus-visible .MuiSvgIcon-root, &:active .MuiSvgIcon-root": {
                    outline: `3px solid ${getFocusOutlineColor(theme)}`,
                    outlineOffset: 1,
                    borderRadius: "50%",
                },
            }),
        }
    }
}